import { useState } from "react";
import PageTemplate from "../../../Templates/PageTemplate";
import middleware from "../../../Api/Middleware";
import { useNavigate } from "react-router";
import Key from "./Icons/key.svg";
import Eye from "./Icons/eye.svg";
import EyeClosed from "./Icons/eye-closed.svg";
import "../EditUsers/EditUsers.css";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCircleInfo } from "@fortawesome/free-solid-svg-icons";
import ToastSuccess from "../../../Toasts/ToastSuccess";
import ToastError from "../../../Toasts/ToastError";
import PasswordRequirementsModal from "../EditUsers/Modals/PasswordRequirementsModal";
import SecurityCodeModal from "../EditUsers/Modals/SecurityCodeModal";
import RoleInfoModal from "../EditUsers/Modals/RoleInfoModal";
import { BackButton, ButtonBlue } from "../../../HouseStyle/Components/Buttons";
import { SUPPORTED_LANGUAGE_OPTIONS } from "../../../../utils";
import ToastPromise from "../../../Toasts/ToastPromise";

const roleOptions = [
  { value: 0, label: "Alleen bestellingen" },
  { value: 1, label: "Medewerker" },
  { value: 2, label: "Leidinggevende" },
  { value: 3, label: "Manager" },
  { value: 4, label: "Admin" },
];

const AddUser = ({ ownUserRole, ownUserId }) => {
  const navigate = useNavigate();
  const [updateUserValues, setUpdateUserValues] = useState({
    first_name: "",
    last_name: "",
    email: "",
    date_of_birth: '',
    password: "",
    security_code: "",
    language: "",
    role: "",
  });
  const [showPassword, setShowPassword] = useState(false);
  const [showSecurityCode, setShowSecurityCode] = useState(false);
  const [emailError, setEmailError] = useState("");
  const [passwordError, setPasswordError] = useState("");
  const [fieldErrors, setFieldErrors] = useState({});
  const [modals, setModals] = useState({
    password: false,
    role: false,
    securityCode: false,
  });
  const [securityCodeError, setSecurityCodeError] = useState("");
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  const passwordRegex =
    /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[@$!%*?&])[A-Za-z\d@$!%*?&]{8,}$/;

  const toggleModal = (modal) =>
    setModals((prev) => ({ ...prev, [modal]: !prev[modal] }));

  const validateEmail = (email) => {
    if (!emailRegex.test(email)) {
      setEmailError("Ongeldig e-mailadres.");
      setFieldErrors((prev) => ({ ...prev, email: true }));
    } else {
      setEmailError("");
      setFieldErrors((prev) => ({ ...prev, email: false }));
    }
  };

  const validatePassword = (password) => {
    if (!passwordRegex.test(password)) {
      setPasswordError(
        "Wachtwoord moet minimaal 8 tekens bevatten, inclusief hoofdletters, kleine letters, een cijfer en een speciaal teken."
      );
      setFieldErrors((prev) => ({ ...prev, password: true }));
    } else {
      setPasswordError("");
      setFieldErrors((prev) => ({ ...prev, password: false }));
    }
  };

  const validateSecurityCode = (code) => {
    const digitRegex = /^\d{4}$/;
    if (!code) return; // Skip validation if empty (unless it's required)
    
    if (!digitRegex.test(code)) {
      setSecurityCodeError("Beveiligingspin moet exact 4 cijfers bevatten.");
      setFieldErrors((prev) => ({ ...prev, security_code: true }));
      return false;
    } else {
      setSecurityCodeError("");
      setFieldErrors((prev) => ({ ...prev, security_code: false }));
      return true;
    }
  };

  const hasRequiredFields = () => {
    const requiredFields = [
      "first_name",
      "last_name",
      "email",
      "password",
      "role",
    ];
    let allFieldsValid = true;
    let missingFields = [];

    requiredFields.forEach((field) => {
      if (!updateUserValues[field] || updateUserValues[field] === "") {
        setFieldErrors((prev) => ({ ...prev, [field]: true }));
        missingFields.push(field);
        allFieldsValid = false;
      } else {
        setFieldErrors((prev) => ({ ...prev, [field]: false }));
      }
    });

    if (emailError !== "") {
      allFieldsValid = false;
    }

    if (passwordError !== "") {
      allFieldsValid = false;
    }

    if (!allFieldsValid) {
      let errorMessage = "Vul alle verplichte velden correct in:\n";
      missingFields.forEach((field) => {
        const fieldName = getFieldLabel(field);
        errorMessage += `- ${fieldName} is verplicht.\n`;
      });
      if (emailError) errorMessage += `- ${emailError}\n`;
      if (passwordError) errorMessage += `- ${passwordError}\n`;
      ToastError(errorMessage);
    }

    return allFieldsValid;
  };

  const getFieldLabel = (field) => {
    const fieldLabels = {
      first_name: "Voornaam",
      last_name: "Achternaam",
      email: "E-mail",
      password: "Wachtwoord",
      role: "Rol",
    };
    return fieldLabels[field] || field;
  };

  const generateRandomPassword = async (event) => {
    event.preventDefault();

    const length = 15; // Minimum length is 8, so 15 is sufficient
    const lowercase = "abcdefghijklmnopqrstuvwxyz";
    const uppercase = "ABCDEFGHIJKLMNOPQRSTUVWXYZ";
    const digits = "0123456789";
    const specialChars = "!@#$%^&*";

    // Ensure each type is included at least once
    let password = [
      lowercase[Math.floor(Math.random() * lowercase.length)],
      uppercase[Math.floor(Math.random() * uppercase.length)],
      digits[Math.floor(Math.random() * digits.length)],
      specialChars[Math.floor(Math.random() * specialChars.length)],
    ];

    // Fill the rest of the password
    const allChars = lowercase + uppercase + digits + specialChars;
    while (password.length < length) {
      password.push(allChars[Math.floor(Math.random() * allChars.length)]);
    }

    // Shuffle the password to ensure randomness
    password = password.sort(() => Math.random() - 0.5).join('');

    // Update state
    setUpdateUserValues({ ...updateUserValues, password });

    try {
      await navigator.clipboard.writeText(password);
    } catch (err) {
      console.error("Failed to copy password: ", err);
    }
  };

  const generateRandomSecurityCode = async event => {
    event.preventDefault();
    const length = 4;
    const charset = "0123456789";
    let code = "";

    for (let i = 0; i < length; i++) {
      const randomIndex = Math.floor(Math.random() * charset.length);
      let char = charset[randomIndex];
      code += char;
    }

    setUpdateUserValues({ ...updateUserValues, security_code: code });

    try {
      await navigator.clipboard.writeText(code);
    } catch (err) {
      console.error('Failed to copy password: ', err);
    }
  }

  const addUser = async () => {
    // Validate security code if provided
    if (updateUserValues.security_code && !validateSecurityCode(updateUserValues.security_code)) {
      ToastError("Beveiligingspin moet exact 4 cijfers bevatten.");
      return;
    }

    if (hasRequiredFields()) {
      await ToastPromise({
        promise: middleware.post("users", updateUserValues).then(() => {
          navigate("/algemene/instellingen/gebruikers");
        }),
        pending: 'Gebruiker toevoegen...',
        successRenderer: () => 'Gebruiker succesvol toegevoegd!',
        errorRenderer: (error) => {
          console.error(error);
          return error?.response?.data?.error || 'Er is iets onverwachts fout gegaan tijdens het aanmaken van de gebruiker.';
        },
        autoClose: 2000
      });
    }
  };

  const changeUser = (column, event) => {
    const value = event.target.value;
    
    // Special handling for security code - only allow numbers and max 4 digits
    if (column === "security_code") {
      // Only allow digits and maximum 4 characters
      const onlyDigits = value.replace(/[^\d]/g, '').slice(0, 4);
      setUpdateUserValues({ ...updateUserValues, [column]: onlyDigits });
      validateSecurityCode(onlyDigits);
    } else {
      setUpdateUserValues({ ...updateUserValues, [column]: value });
      
      if (column === "email") validateEmail(value);
      if (column === "password") validatePassword(value);
      if (value !== "") {
        setFieldErrors((prev) => ({ ...prev, [column]: false }));
      }
    }
  };

  return (
    <>
      <PasswordRequirementsModal
        show={modals.password}
        onHide={() => toggleModal("password")}
      />
      <SecurityCodeModal
        show={modals.securityCode}
        onHide={() => toggleModal("securityCode")}
      />
      <RoleInfoModal show={modals.role} onHide={() => toggleModal("role")} />

      <PageTemplate
        navbarTitle={"Gebruiker toevoegen"}
        pageIsLoading={false}
        backValue={"/algemene/instellingen/gebruikers"}
      >
        <div className="w-100">
          <div className="d-flex flex-row justify-content-between mb-3">
            <BackButton onClickFunction={() => navigate(-1)} text={"Terug"} disabled={false} />
            <ButtonBlue
              text={'Gebruiker toevoegen'}
              onClickFunction={(event) => {
                event.preventDefault();
                addUser();
              }}
            />
          </div>
          <form className="whiteBox parentBoxEditUser p-4" autoComplete="off">
            <div className="d-flex col-12 border-bottom py-4">
              <h5 className="titlesEditUsers inputTitle col-3 ps-3">
                Volledige naam
              </h5>
              <input
                className={`inputFieldEditUser mx-2 col-4 ps-2 ${
                  fieldErrors.first_name ? "error" : ""
                }`}
                type="text"
                placeholder="Voornaam"
                name="first_name"
                onChange={(event) => changeUser("first_name", event)}
                value={updateUserValues.first_name}
              ></input>
              <input
                className={`inputFieldEditUser mx-3 ms-4 col-4 ps-2 ${
                  fieldErrors.last_name ? "error" : ""
                }`}
                type="text"
                placeholder="Achternaam"
                name="last_name"
                onChange={(event) => changeUser("last_name", event)}
                value={updateUserValues.last_name}
              ></input>
            </div>
            <div className="d-flex col-12 border-bottom py-4">
              <h5 className="titlesEditUsers inputTitle col-3 ps-3">
                E-mailadres
              </h5>
              <div className="col-9 d-flex flex-column">
                <div className="d-flex flex-row">
                  <input
                    className={`inputFieldEditUser ms-2 col-9 ps-2 ${
                      fieldErrors.email || emailError ? "error" : ""
                    }`}
                    type="email"
                    name="email"
                    autoComplete="new-password"
                    placeholder="Voer e-mailadres in"
                    onChange={(event) => changeUser("email", event)}
                    value={updateUserValues.email}
                  ></input>
                </div>
                <small className="ms-4 text-secondary">
                  Het is niet mogelijk om uw wachtwoord te herstellen met
                  systeem-e-mail.
                </small>
              </div>
            </div>
            <div className="d-flex col-12 border-bottom py-4">
              <h5 className="titlesEditUsers inputTitle mb-0 col-3 ps-3">
                Geboortedatum
              </h5>
              <div className="col-9 d-flex flex-column">
                <div className="d-flex flex-row">
                  <input
                  className={`inputFieldEditUser mx-2 col-9 ps-2 ${
                    fieldErrors.date_of_birth ? "error" : ""
                  }`}
                  type="date"
                  max={new Date().toISOString().split('T')[0]}
                  name="date_of_birth"
                  onChange={(event) => changeUser("date_of_birth", event)}
                  value={updateUserValues.date_of_birth}
                ></input>
                </div>
              </div>
            </div>
            <div className="d-flex col-12 border-bottom py-4">
              <h5 className="titlesEditUsers inputTitle col-3 ps-3">
                Wachtwoord{" "}
                <FontAwesomeIcon
                  className="iconInfoStyle"
                  onClick={() => toggleModal("password")}
                  icon={faCircleInfo}
                />
              </h5>
              <div className="col-9 d-flex">
                <input
                  className={`inputFieldEditUser ps-2 ms-2 col-10 ${
                    fieldErrors.password || passwordError ? "error" : ""
                  }`}
                  type={showPassword ? "text" : "password"}
                  name="password"
                  autoComplete="new-password"
                  onChange={(event) => changeUser("password", event)}
                  value={updateUserValues.password}
                  placeholder="Voer wachtwoord in"
                  style={{
                    borderTopRightRadius: "0px",
                    borderBottomRightRadius: "0px",
                  }}
                ></input>
                <button
                  className="btn btnPasswordShow"
                  onClick={(event) => {
                    event.preventDefault();
                    setShowPassword(!showPassword);
                  }}
                >
                  <img
                    className="h-100"
                    src={showPassword ? Eye : EyeClosed}
                  />
                </button>
                <button
                  className="btn btnEditUser"
                  onClick={generateRandomPassword}
                >
                  <img className="h-100" src={Key} />
                </button>
              </div>
            </div>
            <div className="d-flex col-12 border-bottom py-4">
              <h5 className="titlesEditUsers inputTitle col-3 ps-3">
                Beveiligingspin instellen{" "}
                <FontAwesomeIcon
                  className="iconInfoStyle"
                  onClick={() => toggleModal("securityCode")}
                  icon={faCircleInfo}
                />
              </h5>
              <div className="col-9 d-flex flex-column">
                <div className="d-flex">
                  <input
                    className={`inputFieldEditUser ps-2 ms-2 col-10 ${
                      fieldErrors.security_code ? "error" : ""
                    }`}
                    type={showSecurityCode ? "text" : "password"}
                    name="security_code" 
                    autoComplete="new-password"
                    value={updateUserValues.security_code}
                    onChange={(event) => changeUser("security_code", event)}
                    placeholder="Beveiligingspin (4 cijfers)"
                    style={{
                      borderTopRightRadius: "0px",
                      borderBottomRightRadius: "0px",
                    }}
                  ></input>
                  <button
                    className="btn btnPasswordShow"
                    onClick={(event) => {
                      event.preventDefault();
                      setShowSecurityCode(!showSecurityCode);
                    }}
                  >
                    <img
                      className="h-100"
                      src={showSecurityCode ? Eye : EyeClosed}
                    />
                  </button>
                  <button
                    className="btn btnEditUser"
                    onClick={generateRandomSecurityCode}
                  >
                    <img className="h-100" src={Key} />
                  </button>
                </div>
                {securityCodeError && (
                  <small className="ms-4 text-danger">{securityCodeError}</small>
                )}
              </div>
            </div>
            <div className="d-flex col-12 border-bottom py-4">
              <h5 className="titlesEditUsers inputTitle mb-0 col-3 ps-3">
                Taal
              </h5>
              <div className="col-9 d-flex flex-column">
                <div className="d-flex flex-row">
                  <select
                    className="inputFieldEditUser ps-2 ms-2 col-11"
                    name="language"
                    onChange={(event) => changeUser("language", event)}
                    value={updateUserValues.language}
                  >
                    <option value="">Selecteer taal</option>
                    {SUPPORTED_LANGUAGE_OPTIONS.map((language, index) => (
                      <option key={index} value={language.code}>
                        {language.lang}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
            </div>
            <div className="d-flex col-12 border-bottom py-4">
              <h5 className="titlesEditUsers inputTitle mb-0 col-3 ps-3">
                Rol{" "}
                <FontAwesomeIcon
                  className="iconInfoStyle"
                  onClick={() => toggleModal("role")}
                  icon={faCircleInfo}
                />
              </h5>
              <div className="col-9 d-flex flex-column">
                <div className="d-flex flex-column">
                  <div className="d-flex flex-column">
                    <select
                      className={`inputFieldEditUser ps-2 ms-2 col-11 ${
                        fieldErrors.role ? "error" : ""
                      }`}
                      value={updateUserValues.role}
                      onChange={(event) => changeUser("role", event)}
                    >
                      <option value="">Selecteer rol</option>
                      {roleOptions.map((role, index) => (
                        <option key={index} value={role.value}>
                          {role.label}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>
              </div>
            </div>
            <div className="d-flex col-12 justify-content-center align-items-center pt-4">
              <ButtonBlue
                text={'Gebruiker toevoegen'}
                onClickFunction={(event) => {
                  event.preventDefault();
                  addUser();
                }}
              />
            </div>
          </form>
        </div>
      </PageTemplate>
    </>
  );
};

export default AddUser;